import type { CaptureResult } from "posthog-js";

// Safari reports failures from cross-origin scripts (including browser
// extensions) to window.onerror without a source or stack. PostHog turns
// those opaque browser notifications into "$exception" events named
// "Script error.", which creates a large, non-actionable issue. Keep real
// exceptions while dropping only this unverifiable browser-generated signal.
const isOpaqueScriptError = (event: {
  event?: string;
  properties?: Record<string, unknown>;
} | null) => {
  if (!event || event.event !== "$exception") return false;

  const values = event.properties?.$exception_values;
  return (
    Array.isArray(values) &&
    values.length === 1 &&
    values[0] === "Script error." &&
    (!event.properties?.$exception_sources ||
      (Array.isArray(event.properties.$exception_sources) &&
        event.properties.$exception_sources.length === 0))
  );
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function beforeSendToPostHog(event: CaptureResult | null): CaptureResult | null {
  if (event?.event !== "$exception") return event;
  if (isOpaqueScriptError(event)) return null;

  const exceptions: unknown = event.properties.$exception_list;
  if (!Array.isArray(exceptions) || exceptions.length !== 1) return event;
  const exception: unknown = exceptions[0];
  if (!isRecord(exception) || exception.type !== "TypeError" || exception.value !== "Type error") {
    return event;
  }
  const stacktrace = exception.stacktrace;
  if (!isRecord(stacktrace) || !Array.isArray(stacktrace.frames) || stacktrace.frames.length !== 1) {
    return event;
  }
  const frame: unknown = stacktrace.frames[0];
  if (!isRecord(frame) || frame.function !== "sortedTrackListForMenu") return event;

  // WebKit's native media-controls method, not an application function:
  // https://developer.apple.com/documentation/webkitjs/mediacontrolshost/1630807-sortedtracklistformenu
  // Only suppress the isolated native signature. Keep source-backed frames,
  // additional frames and exception chains so application failures stay visible.
  const nativeSource = frame.filename == null || frame.filename === "" ||
    frame.filename === "[native code]" || frame.filename === "Unknown Source";
  return nativeSource ? null : event;
}
